import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { askDataPilot, type DPSnapshot, type DPNamedSnapshot, type DPAnomaly } from '../../api/datapilot'
import type { KPIEntry } from '../../data/kpi-glossary'

export interface ChatMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  insights?: string[]
  anomalies?: DPAnomaly[]
  suggested?: string[]
  source?: 'demo' | 'live'
  loading?: boolean
}

interface MetricContext {
  metricId?: string
  metricName: string
  snapshot?: DPSnapshot
  period?: string
  entry?: KPIEntry
}

interface DataPilotContextValue {
  open: boolean
  loading: boolean
  activeMetric: MetricContext | null
  messages: ChatMessage[]
  openForMetric: (ctx: MetricContext) => void
  openChat: () => void
  close: () => void
  ask: (question: string) => void
}

const DataPilotContext = createContext<DataPilotContextValue | null>(null)

export function useDataPilot(): DataPilotContextValue {
  const ctx = useContext(DataPilotContext)
  if (!ctx) throw new Error('useDataPilot must be used within DataPilotProvider')
  return ctx
}

export function DataPilotProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeMetric, setActiveMetric] = useState<MetricContext | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const idRef = useRef(0)
  const metricRef = useRef<MetricContext | null>(null)

  const nextId = () => ++idRef.current

  const runAsk = useCallback(
    async (question: string, opts?: { seedAssistantOnly?: boolean }) => {
      const ctx = metricRef.current
      const placeholderId = nextId()
      setMessages((m) => {
        const base = opts?.seedAssistantOnly
          ? m
          : [...m, { id: nextId(), role: 'user' as const, content: question }]
        return [...base, { id: placeholderId, role: 'assistant' as const, content: '', loading: true }]
      })
      setLoading(true)
      try {
        const res = await askDataPilot({
          metric_id: ctx?.metricId,
          metric_name: ctx?.metricName,
          question,
          period: ctx?.period,
          locale: i18n.language,
          snapshot: ctx?.snapshot,
        })
        setMessages((m) =>
          m.map((msg) =>
            msg.id === placeholderId
              ? {
                  ...msg,
                  loading: false,
                  content: res.answer,
                  insights: res.insights,
                  anomalies: res.anomalies,
                  suggested: res.suggested_questions,
                  source: res.source,
                }
              : msg,
          ),
        )
      } finally {
        setLoading(false)
      }
    },
    [i18n.language],
  )

  const openForMetric = useCallback(
    (ctx: MetricContext) => {
      metricRef.current = ctx
      setActiveMetric(ctx)
      setMessages([])
      setOpen(true)
      // Seed with an automatic explanation of the metric.
      void runAsk('', { seedAssistantOnly: true })
    },
    [runAsk],
  )

  const openChat = useCallback(() => {
    metricRef.current = null
    setActiveMetric(null)
    setOpen(true)
  }, [])

  const close = useCallback(() => setOpen(false), [])

  const ask = useCallback(
    (question: string) => {
      if (!question.trim()) return
      void runAsk(question)
    },
    [runAsk],
  )

  return (
    <DataPilotContext.Provider
      value={{ open, loading, activeMetric, messages, openForMetric, openChat, close, ask }}
    >
      {children}
    </DataPilotContext.Provider>
  )
}

// Helper to assemble a summary request payload from named metric snapshots.
export type { DPNamedSnapshot }
